import {
  Accordion,
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  Chip,
  Container,
  Divider,
  Grid,
  Group,
  Modal,
  NumberFormatter,
  NumberInput,
  Pagination,
  Paper,
  Progress,
  RingProgress,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAlertTriangle,
  IconAt,
  IconBook,
  IconCalendar,
  IconCurrencyPound,
  IconEdit,
  IconEye,
  IconInfoCircle,
  IconPlus,
  IconRefresh,
  IconTag,
  IconTicket,
  IconTrash,
  IconTrendingUp,
  IconUserCheck,
  IconUserX,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useContext, useState } from "react";
import { HttpSuccessCodesObj } from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getCurrencySymbol,
  getFinancialValueFromNumeric,
} from "../../Lib/methods";
import { PaginatedResponse } from "../../Lib/responses";
import { PaginationProps, PlanCouponProps, PlanProps } from "../../Lib/types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import { AppContext } from "../Dashboard";

dayjs.extend(relativeTime);

const PLAN_BASE_CURRENCY_SYMBOL = "£";

const createEmptyCoupon = (): PlanCouponProps => ({
  code: "",
  type: "percentage",
  value: 0,
  isActive: true,
  expiresAt: undefined,
  maxRedemptions: undefined,
  redemptionCount: 0,
});

const Plans: React.FC = () => {
  const context = useContext(AppContext);
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 12,
  });

  const {
    data: plans,
    isLoading: isLoadingPlans,
    reloadData: reloadPlans,
  } = usePerformRequest<PaginatedResponse<PlanProps[]>>({
    method: Endpoints.GetPlans(pagination.page, pagination.limit).method,
    route: Endpoints.GetPlans(pagination.page, pagination.limit).route,
  });

  const [selectedPlan, setSelectedPlan] = useState<PlanProps | null>(null);
  const [
    isDetailModalOpened,
    { open: openDetailModal, close: closeDetailModal },
  ] = useDisclosure(false);
  const [
    isCreateModalOpened,
    { open: openCreateModal, close: closeCreateModal },
  ] = useDisclosure(false);
  const [
    isUpdateModalOpened,
    { open: openUpdateModal, close: closeUpdateModal },
  ] = useDisclosure(false);
  const [
    isDeleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);
  const [isDeletingPlan, setIsDeletingPlan] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  // Plan form for create/update
  const planForm = useForm({
    initialValues: {
      name: "",
      price: 0,
      tag: "",
      description: "",
      thumbnail: "",
      duration: 1,
      coupons: [] as PlanCouponProps[],
    },
    validate: {
      name: (value) =>
        !value || value.length < 3
          ? "Name must be at least 3 characters"
          : null,
      price: (value) =>
        !value || value <= 0 ? "Price must be greater than 0" : null,
      tag: (value) =>
        !value || value.length < 3 ? "Tag must be at least 3 characters" : null,
      description: (value) =>
        !value || value.length < 10
          ? "Description must be at least 10 characters"
          : null,
      duration: (value) =>
        !value || value <= 0 ? "Duration must be at least 1 month" : null,
    },
  });

  const normalizePlanPayload = (values: typeof planForm.values) => ({
    ...values,
    coupons: values.coupons
      .map((coupon) => ({
        ...coupon,
        code: coupon.code.trim().toUpperCase(),
        value: Number(coupon.value),
        maxRedemptions: coupon.maxRedemptions
          ? Number(coupon.maxRedemptions)
          : undefined,
        expiresAt: coupon.expiresAt ? Number(coupon.expiresAt) : undefined,
      }))
      .filter((coupon) => coupon.code && coupon.value > 0),
  });

  const handlePlanClick = (plan: PlanProps) => {
    setSelectedPlan(plan);
    openDetailModal();
  };

  const handleEditClick = (plan: PlanProps, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setSelectedPlan(null);
    setSelectedPlan(plan);
    planForm.setValues({
      name: plan.name,
      price: plan.price,
      tag: plan.tag,
      description: plan.description,
      duration: plan.duration,
      thumbnail: plan.thumbnail ?? "",
      coupons:
        plan.coupons?.map((coupon) => ({
          ...coupon,
          redemptionCount: coupon.redemptionCount ?? 0,
        })) ?? [],
    });
    openUpdateModal();
  };

  const handleDeleteClick = (plan: PlanProps, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setSelectedPlan(plan);
    openDeleteModal();
  };

  const handleCreatePlan = async () => {
    const { hasErrors } = planForm.validate();
    if (hasErrors) return;

    setIsCreatingPlan(true);
    try {
      const r = await PerformRequest({
        method: Endpoints.GetCreatePlan().method,
        route: Endpoints.GetCreatePlan().route,
        data: normalizePlanPayload(planForm.values),
        callback: () => setIsCreatingPlan(false),
      });
      setIsCreatingPlan(false);
      if (r) {
        if (r.status === HttpSuccessCodesObj.Created) {
          DefaultSuccessNotification("Plan created successfully!");
          closeCreateModal();
          reloadPlans();
          planForm.reset();
        }
      }
    } catch (error) {
      DefaultErrorNotification("An error occurred while creating the plan");
    } finally {
      setIsCreatingPlan(false);
    }
  };

  const handleUpdatePlan = async () => {
    const { hasErrors } = planForm.validate();
    if (hasErrors || !selectedPlan) return;

    setIsUpdatingPlan(true);
    try {
      const r = await PerformRequest({
        method: Endpoints.GetUpdatePlan(selectedPlan.id).method,
        route: Endpoints.GetUpdatePlan(selectedPlan.id).route,
        data: normalizePlanPayload(planForm.values),
        callback: () => setIsUpdatingPlan(false),
      });
      setIsUpdatingPlan(false);
      if (r && r.status === HttpSuccessCodesObj.Success) {
        DefaultSuccessNotification("Plan updated successfully!");
        closeUpdateModal();
        reloadPlans();
        planForm.reset();
      } else {
        DefaultErrorNotification(r?.message || "Failed to update plan");
      }
    } catch (error) {
      DefaultErrorNotification("An error occurred while updating the plan");
    } finally {
      setIsUpdatingPlan(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!selectedPlan) return;

    setIsDeletingPlan(true);
    try {
      const r = await PerformRequest({
        method: Endpoints.GetDeletePlan(selectedPlan.id).method,
        route: Endpoints.GetDeletePlan(selectedPlan.id).route,
        data: { deleteResources: false },
        callback: () => setIsDeletingPlan(false),
      });
      setIsDeletingPlan(false);
      if (r && r.status === HttpSuccessCodesObj.Success) {
        DefaultSuccessNotification("Plan deleted successfully!");
        closeDeleteModal();
        reloadPlans();
        setSelectedPlan(null);
      } else {
        DefaultErrorNotification(r?.message || "Failed to delete plan");
      }
    } catch (error) {
      DefaultErrorNotification("An error occurred while deleting the plan");
    } finally {
      setIsDeletingPlan(false);
    }
  };

  // Helper functions
  const getStatusColor = (plan: PlanProps) => {
    return plan.isActive ? "green" : "red";
  };

  const getStatusLabel = (plan: PlanProps) => {
    return plan.isActive ? "Active" : "Inactive";
  };

  const getPlanInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  };

  const renderCouponFields = () => (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <div>
          <Text fw={600} size="sm">
            Coupons
          </Text>
          <Text size="xs" c="dimmed">
            Add optional promo codes for this plan.
          </Text>
        </div>
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={() => planForm.insertListItem("coupons", createEmptyCoupon())}
        >
          Add Coupon
        </Button>
      </Group>

      {planForm.values.coupons.length === 0 ? (
        <Paper p="sm" withBorder radius="md">
          <Text size="sm" c="dimmed">
            No coupon codes configured.
          </Text>
        </Paper>
      ) : (
        planForm.values.coupons.map((coupon, index) => (
          <Paper key={index} p="sm" withBorder radius="md">
            <Stack gap="sm">
              <Group justify="space-between" align="center">
                <Text fw={500} size="sm">
                  Coupon {index + 1}
                </Text>
                <ActionIcon
                  color="red"
                  variant="light"
                  onClick={() => planForm.removeListItem("coupons", index)}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>

              <Group grow align="flex-start">
                <TextInput
                  label="Code"
                  placeholder="WELCOME10"
                  leftSection={<IconTicket size={16} />}
                  {...planForm.getInputProps(`coupons.${index}.code`)}
                />
                <Select
                  label="Type"
                  data={[
                    { label: "Percentage", value: "percentage" },
                    { label: "Fixed GBP", value: "fixed" },
                  ]}
                  {...planForm.getInputProps(`coupons.${index}.type`)}
                />
              </Group>

              <Group grow align="flex-start">
                <NumberInput
                  label="Value"
                  placeholder="10"
                  min={0}
                  hideControls
                  {...planForm.getInputProps(`coupons.${index}.value`)}
                />
                <NumberInput
                  label="Max Uses"
                  placeholder="Unlimited"
                  min={0}
                  hideControls
                  value={planForm.values.coupons[index]?.maxRedemptions ?? ""}
                  onChange={(value) =>
                    planForm.setFieldValue(
                      `coupons.${index}.maxRedemptions`,
                      value === "" ? undefined : Number(value)
                    )
                  }
                />
              </Group>

              <Group grow align="flex-end">
                <TextInput
                  label="Expiry Date"
                  type="date"
                  value={
                    planForm.values.coupons[index]?.expiresAt
                      ? dayjs(planForm.values.coupons[index].expiresAt).format(
                          "YYYY-MM-DD"
                        )
                      : ""
                  }
                  onChange={(event) =>
                    planForm.setFieldValue(
                      `coupons.${index}.expiresAt`,
                      event.currentTarget.value
                        ? dayjs(event.currentTarget.value)
                            .endOf("day")
                            .valueOf()
                        : undefined
                    )
                  }
                />
                <Checkbox
                  label="Active"
                  checked={planForm.values.coupons[index]?.isActive ?? true}
                  onChange={(event) =>
                    planForm.setFieldValue(
                      `coupons.${index}.isActive`,
                      event.currentTarget.checked
                    )
                  }
                  pb={8}
                />
              </Group>
            </Stack>
          </Paper>
        ))
      )}
    </Stack>
  );
  const totalPlans = plans?.totalRecords || 0;
  const activePlans = plans?.data?.filter((p) => p.isActive).length || 0;
  const inactivePlans = plans?.data?.filter((p) => !p.isActive).length || 0;
  const totalRevenue =
    plans?.data?.reduce((sum, plan) => sum + plan.price, 0) || 0;

  const StatCard = ({ title, value, icon, color, description }: any) => (
    <Card shadow="sm" padding="xl" radius="md" withBorder>
      <Group justify="space-between">
        <div>
          <Text c="dimmed" size="sm" fw={500} tt="uppercase">
            {title}
          </Text>
          {isLoadingPlans ? (
            <Skeleton height={24} width={80} my={4} />
          ) : (
            <Text fw={700} size="xl">
              {title.includes("alue") ? (
                // <NumberFormatter value={value} thousandSeparator prefix="$" />
                <Text fw={700} size="xl">
                  {value}
                </Text>
              ) : (
                <NumberFormatter value={value} thousandSeparator />
              )}
            </Text>
          )}
          {description && (
            <Text c="dimmed" size="xs" mt={4}>
              {description}
            </Text>
          )}
        </div>
        <ThemeIcon color={color} size={60} radius="md">
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );

  return (
    <div style={{ padding: "32px", background: "#f8f9fa", minHeight: "100vh" }}>
      <Container size="xl">
        {/* Header */}
        <Group justify="space-between" align="center" mb={32}>
          <div>
            <Text size="xl" fw={700} mb={4}>
              Plan Management
            </Text>
            <Text c="dimmed" size="sm">
              Create and manage subscription plans for your platform
            </Text>
          </div>
          <Group gap={12}>
            <Divider orientation="vertical" />
            <Button
              leftSection={<IconPlus size={16} />}
              variant="filled"
              color="green"
              onClick={() => {
                openCreateModal();
                planForm.reset();
              }}
              disabled={context?.profile?.role !== "superadmin"}
            >
              Create Plan
            </Button>
            <Button
              leftSection={<IconRefresh size={16} />}
              variant="outline"
              color="blue"
              onClick={reloadPlans}
              loading={isLoadingPlans}
            >
              Reload
            </Button>
          </Group>
        </Group>

        {/* Stats Cards */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg" mb={32}>
          <StatCard
            title="Total Plans"
            value={totalPlans}
            icon={<IconBook size={30} />}
            color="blue"
            description="All subscription plans"
          />
          <StatCard
            title="Active Plans"
            value={activePlans}
            icon={<IconUserCheck size={30} />}
            color="green"
            description="Currently active plans"
          />
          <StatCard
            title="Inactive Plans"
            value={inactivePlans}
            icon={<IconUserX size={30} />}
            color="red"
            description="Paused or disabled plans"
          />
          <StatCard
            title="Total Value"
            value={`${PLAN_BASE_CURRENCY_SYMBOL}${getFinancialValueFromNumeric(totalRevenue)}`}
            icon={<IconCurrencyPound size={30} />}
            color="violet"
            description="Combined plan value"
          />
        </SimpleGrid>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(value) => setActiveTab(value || "all")}
          mb={24}
        >
          <Tabs.List grow>
            <Tabs.Tab value="all" leftSection={<IconBook size={16} />}>
              All Plans ({totalPlans})
            </Tabs.Tab>
            <Tabs.Tab
              value="analytics"
              leftSection={<IconTrendingUp size={16} />}
            >
              Analytics
            </Tabs.Tab>
          </Tabs.List>

          {/* All Plans Tab */}
          <Tabs.Panel value="all" pt="lg">
            {isLoadingPlans ? (
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="lg">
                {Array.from({ length: 8 }).map((_, index) => (
                  <Skeleton key={index} height={280} radius="md" />
                ))}
              </SimpleGrid>
            ) : plans?.data && plans.data.length > 0 ? (
              <>
                <Grid gutter="lg">
                  {plans.data.map((plan) => (
                    <Grid.Col
                      key={plan.id}
                      span={{ base: 12, sm: 6, lg: 4, xl: 3 }}
                    >
                      <Card
                        shadow="sm"
                        padding="lg"
                        radius="md"
                        withBorder
                        style={{
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          height: "100%",
                        }}
                        onClick={() => handlePlanClick(plan)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-4px)";
                          e.currentTarget.style.boxShadow =
                            "0 8px 25px rgba(0,0,0,0.12)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "";
                        }}
                      >
                        <Stack gap="md">
                          <Group justify="space-between" align="flex-start">
                            <Avatar
                              size={60}
                              radius="md"
                              style={{ border: "3px solid #e9ecef" }}
                            >
                              {getPlanInitials(plan.name)}
                            </Avatar>
                            <Badge
                              color={getStatusColor(plan)}
                              variant="light"
                              size="sm"
                              radius="md"
                            >
                              {getStatusLabel(plan)}
                            </Badge>
                          </Group>

                          <div>
                            <Text fw={600} size="lg" lineClamp={1} mb={4}>
                              {plan.name}
                            </Text>
                            <Text c="dimmed" size="sm" lineClamp={1} mb={8}>
                              {plan.description}
                            </Text>
                            <Chip
                              size="xs"
                              variant="light"
                              color="blue"
                              radius="md"
                            >
                              {plan.tag}
                            </Chip>
                          </div>

                          <Divider />

                          <Group justify="space-between" align="center">
                            <div>
                              <Text size="xs" c="dimmed" mb={2}>
                                Price
                              </Text>
                              <Text fw={700} size="lg" c="green">
                                {PLAN_BASE_CURRENCY_SYMBOL}
                                {getFinancialValueFromNumeric(plan.price)}
                              </Text>
                            </div>
                            <Group gap={4}>
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                color="blue"
                              >
                                <IconEye size={14} />
                              </ActionIcon>
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                color="yellow"
                                onClick={(e) => handleEditClick(plan, e)}
                              >
                                <IconEdit size={14} />
                              </ActionIcon>
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                display="none"
                                color="red"
                                onClick={(e) => handleDeleteClick(plan, e)}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Group>
                          </Group>

                          <Group justify="space-between" align="center">
                            <div>
                              <Text size="xs" c="dimmed" mb={2}>
                                Duration
                              </Text>
                              <Text fw={600} size="sm" c="blue">
                                {plan.duration}{" "}
                                {plan.duration === 1 ? "month" : "months"}
                              </Text>
                            </div>
                          </Group>

                          <Text size="xs" c="dimmed" ta="center">
                            Created{" "}
                            {dayjs(plan.createdAt).format("DD MMM YYYY")}
                          </Text>
                        </Stack>
                      </Card>
                    </Grid.Col>
                  ))}
                </Grid>

                {/* Pagination */}
                {totalPlans > pagination.limit && (
                  <Group justify="center" mt={32}>
                    <Pagination
                      total={Math.ceil(totalPlans / pagination.limit)}
                      value={pagination.page}
                      onChange={(page) =>
                        setPagination({ ...pagination, page })
                      }
                      size="md"
                      radius="md"
                      withEdges
                    />
                  </Group>
                )}
              </>
            ) : (
              <Paper
                style={{
                  textAlign: "center",
                  padding: "48px 24px",
                  background: "#f8f9fa",
                  border: "1px solid #e9ecef",
                  borderRadius: 12,
                }}
              >
                <IconBook
                  size={64}
                  style={{ marginBottom: 16, opacity: 0.5 }}
                />
                <Text size="lg" fw={500} mb={8}>
                  No plans found
                </Text>
                <Text size="sm" c="dimmed" mb={16}>
                  Create your first subscription plan to get started.
                </Text>
                <Button
                  leftSection={<IconPlus size={16} />}
                  onClick={openCreateModal}
                  disabled={context?.profile?.role !== "superadmin"}
                >
                  Create First Plan
                </Button>
              </Paper>
            )}
          </Tabs.Panel>

          {/* Analytics Tab */}
          <Tabs.Panel value="analytics" pt="lg">
            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="xl">
              <Card shadow="sm" padding="xl" radius="md" withBorder>
                <Text fw={600} mb="lg">
                  Plan Status Distribution
                </Text>
                <RingProgress
                  size={200}
                  thickness={20}
                  sections={[
                    {
                      value: (activePlans / totalPlans) * 100,
                      color: "green",
                    },
                    {
                      value: (inactivePlans / totalPlans) * 100,
                      color: "red",
                    },
                  ]}
                  label={
                    <Text ta="center" fw={700} size="xl">
                      {Math.round((activePlans / totalPlans) * 100)}%
                    </Text>
                  }
                />
                <Text ta="center" c="dimmed" size="sm" mt="md">
                  {activePlans} of {totalPlans} plans active
                </Text>
              </Card>

              <Card shadow="sm" padding="xl" radius="md" withBorder>
                <Text fw={600} mb="lg">
                  Revenue Distribution
                </Text>
                <Stack gap="md">
                  {plans?.data?.slice(0, 5).map((plan) => (
                    <Group key={plan.id} justify="space-between">
                      <Group gap={8}>
                        <Avatar size="sm" radius="sm">
                          {getPlanInitials(plan.name)}
                        </Avatar>
                        <Text size="sm" fw={500}>
                          {plan.name}
                        </Text>
                      </Group>
                      <Progress
                        value={(plan.price / totalRevenue) * 100}
                        size="sm"
                        style={{ flex: 1, marginLeft: 16 }}
                      />
                      <Text size="sm" fw={500}>
                        {PLAN_BASE_CURRENCY_SYMBOL}
                        {getFinancialValueFromNumeric(plan.price)}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              </Card>
            </SimpleGrid>
          </Tabs.Panel>
        </Tabs>

        {/* Plan Detail Modal */}
        <Modal
          opened={isDetailModalOpened}
          onClose={closeDetailModal}
          title={
            <Group gap={12}>
              <Avatar size={40} radius="md">
                {selectedPlan ? getPlanInitials(selectedPlan.name) : ""}
              </Avatar>
              <div>
                <Text fw={600}>{selectedPlan?.name}</Text>
                <Text size="sm" c="dimmed">
                  {selectedPlan?.tag}
                </Text>
              </div>
            </Group>
          }
          size="lg"
          centered
          radius="md"
        >
          {selectedPlan && (
            <Stack gap="lg">
              <Group justify="space-between">
                <Badge
                  color={getStatusColor(selectedPlan)}
                  variant="light"
                  size="lg"
                >
                  {getStatusLabel(selectedPlan)}
                </Badge>
                <Group gap={8}>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => {
                      closeDetailModal();
                      handleEditClick(selectedPlan!);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="xs"
                    color="red"
                    variant="outline"
                    onClick={() => {
                      closeDetailModal();
                      handleDeleteClick(selectedPlan!);
                    }}
                  >
                    Delete
                  </Button>
                </Group>
              </Group>

              <Accordion defaultValue="details">
                <Accordion.Item value="details">
                  <Accordion.Control>Plan Details</Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Price
                        </Text>
                        <Text size="sm" fw={500} c="green">
                          {PLAN_BASE_CURRENCY_SYMBOL}
                          {getFinancialValueFromNumeric(selectedPlan.price)}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Duration
                        </Text>
                        <Text size="sm" fw={500} c="blue">
                          {selectedPlan.duration}{" "}
                          {selectedPlan.duration === 1 ? "month" : "months"}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Tag
                        </Text>
                        <Text size="sm" fw={500}>
                          {selectedPlan.tag}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Created
                        </Text>
                        <Text size="sm" fw={500}>
                          {dayjs(selectedPlan.createdAt).format("DD MMM YYYY")}
                        </Text>
                      </Group>
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
                <Accordion.Item value="description">
                  <Accordion.Control>Description</Accordion.Control>
                  <Accordion.Panel>
                    <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                      {selectedPlan.description}
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>
                <Accordion.Item value="coupons">
                  <Accordion.Control>
                    Coupons ({selectedPlan.coupons?.length ?? 0})
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="sm">
                      {selectedPlan.coupons?.length ? (
                        selectedPlan.coupons.map((coupon) => (
                          <Paper key={coupon.code} p="sm" withBorder radius="md">
                            <Group justify="space-between" align="center">
                              <div>
                                <Text fw={600} size="sm">
                                  {coupon.code}
                                </Text>
                                <Text size="xs" c="dimmed">
                                  {coupon.type === "percentage"
                                    ? `${coupon.value}% off`
                                    : `${PLAN_BASE_CURRENCY_SYMBOL}${coupon.value} off`}
                                </Text>
                              </div>
                              <Group gap={8}>
                                <Badge
                                  color={coupon.isActive ? "green" : "gray"}
                                  variant="light"
                                >
                                  {coupon.isActive ? "Active" : "Inactive"}
                                </Badge>
                                <Badge color="blue" variant="light">
                                  Used {coupon.redemptionCount}
                                  {coupon.maxRedemptions
                                    ? `/${coupon.maxRedemptions}`
                                    : ""}
                                </Badge>
                              </Group>
                            </Group>
                          </Paper>
                        ))
                      ) : (
                        <Text size="sm" c="dimmed">
                          No coupon codes configured for this plan.
                        </Text>
                      )}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Stack>
          )}
        </Modal>

        {/* Create Plan Modal */}
        <Modal
          opened={isCreateModalOpened}
          onClose={closeCreateModal}
          title={
            <Group gap={8}>
              <IconPlus size={20} />
              <Text fw={600}>Create New Plan</Text>
            </Group>
          }
          size="md"
          centered
          radius="md"
        >
          <form onSubmit={planForm.onSubmit(handleCreatePlan)}>
            <Stack gap="md">
              <TextInput
                label="Plan Name"
                placeholder="Enter plan name"
                {...planForm.getInputProps("name")}
                leftSection={<IconAt size={16} />}
                radius="md"
              />
              <NumberInput
                label="Price (In GBP)"
                placeholder="0.00"
                {...planForm.getInputProps("price")}
                leftSection={<IconCurrencyPound size={16} />}
                min={0}
                hideControls
                radius="md"
              />
              <NumberInput
                label={
                  <Group gap={8}>
                    <Text fz="sm" fw={500}>
                      Duration (months)
                    </Text>
                    <Tooltip label="Duration refers to the number of billing cycles in Months">
                      <IconInfoCircle size={16} style={{ cursor: "help" }} />
                    </Tooltip>
                  </Group>
                }
                placeholder="1"
                {...planForm.getInputProps("duration")}
                leftSection={<IconCalendar size={16} />}
                min={1}
                radius="md"
              />
              <TextInput
                label="Tag"
                placeholder="E.g: Perfect for beginners"
                {...planForm.getInputProps("tag")}
                leftSection={<IconTag size={16} />}
                radius="md"
              />
              <Textarea
                label="Description"
                placeholder="Enter detailed plan description"
                {...planForm.getInputProps("description")}
                rows={4}
                radius="md"
              />
              {renderCouponFields()}

              <div className="flex-row align-center justify-between width-100">
                <Button w="48%" variant="outline" onClick={closeCreateModal}>
                  Cancel
                </Button>
                <Button w="48%" type="submit" loading={isCreatingPlan}>
                  Create Plan
                </Button>
              </div>
            </Stack>
          </form>
        </Modal>

        {/* Update Plan Modal */}
        <Modal
          opened={isUpdateModalOpened}
          onClose={closeUpdateModal}
          title={
            <Group gap={8}>
              <IconEdit size={20} />
              <Text fw={600}>Update Plan</Text>
            </Group>
          }
          size="md"
          centered
          radius="md"
        >
          <form onSubmit={planForm.onSubmit(handleUpdatePlan)}>
            <Stack gap="md">
              <TextInput
                label="Plan Name"
                placeholder="Enter plan name"
                {...planForm.getInputProps("name")}
                leftSection={<IconAt size={16} />}
                radius="md"
              />
              <NumberInput
                label="Price"
                placeholder="0.00"
                {...planForm.getInputProps("price")}
                leftSection={<IconCurrencyPound size={16} />}
                min={0}
                disabled
                hideControls
                radius="md"
              />
              <NumberInput
                label="Duration (months)"
                placeholder="1"
                {...planForm.getInputProps("duration")}
                leftSection={<IconCalendar size={16} />}
                min={1}
                disabled
                radius="md"
              />
              <TextInput
                label="Tag"
                placeholder="E.g: Perfect for beginners"
                {...planForm.getInputProps("tag")}
                leftSection={<IconTag size={16} />}
                radius="md"
              />
              <Textarea
                label="Description"
                placeholder="Enter detailed plan description"
                {...planForm.getInputProps("description")}
                rows={4}
                radius="md"
              />
              {renderCouponFields()}

              <div className="flex-row align-center justify-between width-100">
                <Button w="48%" variant="outline" onClick={closeUpdateModal}>
                  Cancel
                </Button>
                <Button w="48%" type="submit" loading={isUpdatingPlan}>
                  Update Plan
                </Button>
              </div>
            </Stack>
          </form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={isDeleteModalOpened}
          onClose={closeDeleteModal}
          title={
            <Group align="center" gap={8}>
              <IconAlertTriangle size={20} color="#fa5252" />
              <Text fw={600} size="lg" c="red">
                Delete Plan
              </Text>
            </Group>
          }
          centered
          radius="md"
          overlayProps={{
            backgroundOpacity: 0.55,
            blur: 3,
          }}
        >
          <Stack gap="lg">
            <div>
              <Text size="sm" c="dimmed" mb={12}>
                Are you sure you want to permanently delete this plan? This
                action cannot be undone.
              </Text>

              {selectedPlan && (
                <Paper
                  style={{
                    background: "#fff5f5",
                    padding: 16,
                    borderRadius: 8,
                    border: "1px solid #fec2c2",
                  }}
                >
                  <Group align="center" gap={8} mb={8}>
                    <Avatar size={32} radius="md">
                      {getPlanInitials(selectedPlan.name)}
                    </Avatar>
                    <div>
                      <Text fw={500} size="sm" c="red">
                        {selectedPlan.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {selectedPlan.tag}
                      </Text>
                    </div>
                  </Group>
                  <Text size="xs" c="red" fw={500}>
                    ⚠️ All plan data and associated subscriptions will be
                    permanently deleted
                  </Text>
                </Paper>
              )}
            </div>

            <Group justify="flex-end" gap={12}>
              <Button variant="outline" color="gray" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleDeletePlan}
                loading={isDeletingPlan}
                leftSection={<IconTrash size={16} />}
              >
                {isDeletingPlan ? "Deleting..." : "Delete Plan"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </div>
  );
};

export default Plans;
